import { useState } from 'react'

import {
  Box,
  Grid,
  Spinner,
  Text,
  WizardStepRenderParams,
} from '@factorialco/deprecated-design-system'

import TextField from 'modules/core/components/TextField'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import i18n from 'modules/core/lib/i18n'

import { WizardData } from 'modules/documents/components/BulkFiles/DocumentTemplateStepsModal/types'
import TemplateAccessWarning from 'modules/documents/components/BulkFiles/TemplateAccessWarning'
import EsignatureSettings from 'modules/documents/components/BulkFiles/_steps/EsignatureSettings'
import DocumentTemplatesSelector from 'modules/documents/components/DocumentTemplates/DocumentTemplatesSelector'
import FolderSelector from 'modules/documents/components/Folders/FolderSelector'
import useDocumentContextlessPolicies from 'modules/documents/hooks/useDocumentContextlessPolicies'
import {
  DocumentTemplate,
  useGetDocumentTemplates,
} from 'modules/documents/hooks/useGetDocumentTemplates'
import { Folder } from 'modules/documents/hooks/useGetFolders'
import getVariablesAccess from 'modules/documents/lib/getVariablesAccess'
import type { VariablesAccess } from 'modules/documents/lib/getVariablesAccess'
import variables from 'modules/documents/store/collections/variables'

interface Props {
  folders: Folder[]
}

const TemplateSettings = ({
  data,
  onDataChanged,
  folders,
}: WizardStepRenderParams<WizardData> & Props) => {
  const { combineFileName, esignatureRequest, additionalSignersAccessIds } = data
  const { documentTemplates, loading: loadingTemplates } = useGetDocumentTemplates()
  const { loading: loadingVariables } = useAsyncData(() => variables.allVariables(), [])
  const { loading: loadingPolicies, policies } = useDocumentContextlessPolicies()
  const [documentTemplateId, setDocumentTemplateId] = useState<string | null | undefined>()
  const [templateAccess, setTemplateAccess] = useState<VariablesAccess>()

  const loading = loadingTemplates || loadingPolicies || loadingVariables

  const canManageDefaultEmployeeFolder = policies?.employeeManageDefaultEmployeeFolder.can

  const onChange = (template: DocumentTemplate) => {
    const access = getVariablesAccess(template, variables.models)
    const templateDisabled = access != 'full' && access != 'partial'

    setTemplateAccess(access)
    setDocumentTemplateId(template.id)
    onDataChanged({
      ...data,
      templateId: template.id,
      templateDisabled: templateDisabled,
      combineFileName: template.name,
      extension: template.extension,
    })
  }

  if (loading)
    return (
      <Box padding="s40" width="full" alignItems="center">
        <Spinner />
      </Box>
    )

  return (
    <Grid variant="fixed">
      <Box gap="s16" gridColSpan={{ xxs: 6 }}>
        <Text.Normal weight={'bold'}>
          {i18n.t('split_documents.upload_process.workflows.template_settings.title')}
        </Text.Normal>
        <DocumentTemplatesSelector
          label={i18n.t(
            'split_documents.upload_process.workflows.template_settings.document_template'
          )}
          value={documentTemplateId}
          documentTemplates={documentTemplates}
          placeholder={i18n.t(
            'split_documents.upload_process.workflows.template_settings.placeholder'
          )}
          onSelect={(template) => onChange(template)}
        />
        <Text.Normal weight={'bold'}>
          {i18n.t('split_documents.upload_process.workflows.template_settings.settings')}
        </Text.Normal>
        <TextField
          label={i18n.t('split_documents.form.file_name')}
          value={combineFileName}
          mandatory
          onChange={(newFileName) => onDataChanged({ ...data, combineFileName: newFileName })}
        />
        <FolderSelector
          folders={folders}
          label={i18n.t('split_documents.form.select_label')}
          onChange={(folderId) => onDataChanged({ ...data, folderId })}
          noneOption={canManageDefaultEmployeeFolder}
          tooltip={i18n.t('split_documents.assign_documents.folder_help_tooltip')}
        />
        <EsignatureSettings
          settings={{
            newEsignatureRequest: esignatureRequest,
            newAdditionalSignersAccessIds: additionalSignersAccessIds,
          }}
          onChange={({ newEsignatureRequest, newAdditionalSignersAccessIds }) =>
            onDataChanged({
              ...data,
              esignatureRequest: newEsignatureRequest,
              additionalSignersAccessIds: newAdditionalSignersAccessIds,
            })
          }
        />
      </Box>
      <Box gridColSpan={{ xxs: 6 }} paddingTop="s32">
        {templateAccess && templateAccess !== 'full' && (
          <TemplateAccessWarning access={templateAccess} />
        )}
      </Box>
    </Grid>
  )
}

export default TemplateSettings
